/* eslint-disable max-len */
/* eslint-disable react/jsx-props-no-spreading */
import {
    MutableRefObject,
    useEffect,
    useCallback,
    useMemo,
} from 'react'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import {
    DataGridPro,
    GridCallbackDetails,
    GridColumnVisibilityModel,
    GridDataSourceCacheDefault,
    GridLogicOperator,
    GridPaginationModel,
    useGridApiRef,
} from '@mui/x-data-grid-pro'

import SkyBaseDataGridToolbar
    from './components/SkyBaseDataGridToolbar/SkyBaseDataGridToolbar'
import {
    filterColumns,
    getColumnForNewFilter,
} from './components/FilterIcon/filterMenuProps'
import NoRowsErrorOverlay from './components/NoRowsErrorOverlay'
import {
    ExtendedGridApiPro,
    SkyBaseDataGridProps,
} from './SkyBaseDataGrid.types'
import {
    PAGE_SIZE_OPTIONS,
    PAGE_SIZE_DEFAULT,
    DELAY_TIMEOUT,
    HEADER_HEIGHT,
    ROW_HEIGHT,
} from './constants'
import FilterIcon from './components/FilterIcon'
import ColumnMenuWithoutFilter from './components/ColumnMenuWithoutFilter'
import {
    getVisibleAndToggableColumns,
} from './utils/FilterModel.utils'
import usePersistedFilterModel from './hooks/usePersistedFilterModel'
import usePersistedSortingModel from './hooks/usePersistedSortingModel'
import SkyBaseDataGridFooter from './components/SkyBaseDataGridFooter'
import usePersistedColumnsState from './hooks/usePersistedColumnsState'

import useDataGridStyles from './SkyBaseDataGrid.styles'

const isRowSelectable = () => {
    // IMPORTANT: returning `true` ensures that "select all" checkbox is synchronized with our state
    // and works properly with page-scoped selection
    return true
}

export default function SkyBaseDataGrid({
    persistentKey,
    CustomToolbar,
    Pagination,
    CustomFooter,
    classNames,
    columns,
    customDataSource,
    gridApiRef,
    height,
    onRowClick,
    onRowSelectionModelChange,
    disableRowSelectionOnClick,
    rowHeight = 'auto',
    rowSelectionModel,
    showToolbar = true,
    slotProps,
    slots,
    visibilityModel,
    onColumnVisibilityModelChange,
    toolbarProps,
    customColumnsSetStateConverter,
    customColumnsInitStateConverter,
    enableSortFilterPersistence = true,
    getDetailPanelContent,
    getDetailPanelHeight,
    onDetailPanelExpandedRowIdsChange,
    paginationModel,
    onPaginationModelChange,
    checkboxSelection,
    disableMultipleRowSelection,
    disabled,
    getRowId,
}: Readonly<SkyBaseDataGridProps>) {
    const newApiRef = useGridApiRef<ExtendedGridApiPro>()
    const apiRef: MutableRefObject<ExtendedGridApiPro> = gridApiRef || newApiRef
    const {
        dataSource,
        onDataSourceError,
        errorMessage,
        key,
    } = customDataSource

    const {
        classes: dataGridClasses, cx,
    } = useDataGridStyles({
        disabled,
    })

    const lowTTLCache = new GridDataSourceCacheDefault({
        ttl: 1000 * 100,
    })

    const getRowHeight = useCallback(() => {
        return rowHeight
    }, [rowHeight])

    const goToPage = useCallback((e, page: number) => {
        apiRef?.current?.setPage(page - 1)
    }, [apiRef])

    const resetToFirstPage = useCallback(() => {
        goToPage(null, 1)
        // Please leave this as empty dep array to avoid re-rendering bugs of filter panel
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    const [
        localFilterModel,
        onFilterModelChange,
    ] = usePersistedFilterModel({
        persistentKey,
        columns,
        enablePersistence: enableSortFilterPersistence,
    })

    const [
        localSortModel,
        onLocalSortModelChange,
    ] = usePersistedSortingModel({
        persistentKey,
        enablePersistence: enableSortFilterPersistence,
    })

    const noRowsAndErrorOverlay = useCallback(() => {
        return (
            <NoRowsErrorOverlay
                error={errorMessage}
            />
        )
    }, [errorMessage])

    const needFilters = useMemo(() => {
        return columns.some((col) => {
            return col.filterable
        })
    }, [columns])

    const DefaultToolbar = useCallback(() => {
        return (
            <SkyBaseDataGridToolbar
                showFilterButton={needFilters}
                {...toolbarProps}
            />
        )
    }, [
        toolbarProps,
        needFilters,
    ])

    const gridSlots = useMemo(() => {
        return {
            noRowsOverlay: noRowsAndErrorOverlay,
            columnHeaderFilterIconButton: FilterIcon,
            columnMenu: ColumnMenuWithoutFilter,
            toolbar: CustomToolbar || DefaultToolbar,
            filterPanelRemoveAllIcon: () => {
                return null
            },
            pagination: Pagination,
            footer: CustomFooter || SkyBaseDataGridFooter,
            ...slots,
        }
    }, [
        noRowsAndErrorOverlay,
        CustomToolbar,
        DefaultToolbar,
        Pagination,
        CustomFooter,
        slots,
    ])

    const gridSlotProps = useMemo(() => {
        return {
            toolbar: {
                quickFilterProps: {
                    debounceMs: DELAY_TIMEOUT,
                },
            },
            loadingOverlay: {
                variant: 'skeleton',
                noRowsVariant: 'skeleton',
            },
            filterPanel: {
                logicOperators: [GridLogicOperator.And],
                className: dataGridClasses.filterPanel,
                filterFormProps: {
                    filterColumns,
                    logicOperatorInputProps: {
                        className: dataGridClasses.filterPanelHiddenElement,
                    },
                    operatorInputProps: {
                        readOnly: true,
                        className: dataGridClasses.filterPanelHiddenElement,
                    },
                    columnInputProps: {
                        variant: 'standard',
                        className: dataGridClasses.filterPanelColumnInput,
                    },
                    deleteIconProps: {
                        className: dataGridClasses.filterPanelDeleteIcon,
                    },
                },
                getColumnForNewFilter,
            },
            columnsManagement: {
                getTogglableColumns: getVisibleAndToggableColumns,
            },
            ...slotProps,
        }
    }, [
        dataGridClasses.filterPanel,
        dataGridClasses.filterPanelColumnInput,
        dataGridClasses.filterPanelDeleteIcon,
        dataGridClasses.filterPanelHiddenElement,
        slotProps,
    ])

    useEffect(() => {
        if (!apiRef.current) return
        apiRef.current.goToPage = goToPage
        apiRef.current.resetToFirstPage = resetToFirstPage
    }, [
        apiRef,
        goToPage,
        resetToFirstPage,
    ])

    const {
        initialState: initialStateColumns,
        onColumnStateChange,
    } = usePersistedColumnsState({
        persistentKey,
        dataSourceKey: key,
        apiRef,
        setStateConverter: customColumnsSetStateConverter,
        initStateConverter: customColumnsInitStateConverter,
    })

    const onColumnVisibilityModelChangeHandler = useCallback((model: GridColumnVisibilityModel) => {
        onColumnVisibilityModelChange(model, true)
    }, [onColumnVisibilityModelChange])

    const initialStateSaved = useMemo(() => {
        return {
            columns: initialStateColumns,
            pagination: !paginationModel ? {
                paginationModel: {
                    pageSize: PAGE_SIZE_DEFAULT,
                },
            } : undefined,
        }
    }, [
        initialStateColumns,
        paginationModel,
    ])

    const onPaginationModelChangeHandler = useCallback((model: GridPaginationModel, details: GridCallbackDetails) => {
        // fix for pagination change called with undefined reason when exporting to PDF
        // undefined reason was resetting the page to 0
        if (onPaginationModelChange && details.reason) {
            onPaginationModelChange(model)
        }
    }, [onPaginationModelChange])

    if (!initialStateColumns) {
        return <CircularProgress />
    }
    return (
        <div className={classNames?.container}>
            <Box
                className={cx(
                    dataGridClasses.dataGridContainer,
                    classNames?.tableWrapper,
                )}
                sx={{
                    height,
                }}
            >
                <DataGridPro
                    columns={columns}
                    rowHeight={ROW_HEIGHT}
                    showToolbar={showToolbar}
                    dataSource={dataSource}
                    onDataSourceError={onDataSourceError}
                    dataSourceCache={lowTTLCache}
                    apiRef={apiRef}
                    density="compact"
                    pagination
                    keepNonExistentRowsSelected
                    slots={gridSlots}
                    slotProps={gridSlotProps}
                    localeText={{
                        filterPanelRemoveAll: 'Clear all filters',
                        filterPanelColumns: 'Column',
                    }}
                    getRowHeight={getRowHeight}
                    sortModel={localSortModel}
                    onSortModelChange={onLocalSortModelChange}
                    filterModel={localFilterModel}
                    onFilterModelChange={onFilterModelChange}
                    columnVisibilityModel={visibilityModel}
                    onColumnVisibilityModelChange={onColumnVisibilityModelChangeHandler}
                    onRowClick={onRowClick}
                    rowSelectionModel={rowSelectionModel}
                    onRowSelectionModelChange={onRowSelectionModelChange}
                    initialState={initialStateSaved}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    hideFooterSelectedRowCount
                    onColumnOrderChange={onColumnStateChange}
                    onColumnWidthChange={onColumnStateChange}
                    getDetailPanelContent={getDetailPanelContent}
                    getDetailPanelHeight={getDetailPanelHeight}
                    onDetailPanelExpandedRowIdsChange={onDetailPanelExpandedRowIdsChange}
                    paginationModel={paginationModel}
                    onPaginationModelChange={onPaginationModelChangeHandler}
                    checkboxSelection={checkboxSelection}
                    disableMultipleRowSelection={disableMultipleRowSelection}
                    isRowSelectable={isRowSelectable}
                    getRowId={getRowId}
                    columnHeaderHeight={HEADER_HEIGHT}
                    disableRowSelectionOnClick={disableRowSelectionOnClick}
                />
            </Box>
        </div>
    )
}
